import type { NameserviceClient, UseNameserviceClientReturn } from '../../types'
import { OpenapiError, useCoreConfig, useOpenapi, useQuery, useSessionExpiry, USERNAME_KEY } from '#imports'

const SERVER_TIMEOUT_MS = 1_000

/**
 * Runs `request` with a signal that aborts after `SERVER_TIMEOUT_MS` or when `signal` does.
 * Not `AbortSignal.timeout`, whose timer outlives a fast answer, nor `AbortSignal.any`, which
 * can retain its signals in Node: on the server both would pile up across renders.
 */
async function withServerTimeout<T>(signal: AbortSignal | undefined, request: (signal: AbortSignal) => Promise<T>): Promise<T> {
    const controller = new AbortController()
    const forwardAbort = () => controller.abort(signal?.reason)
    const timer = setTimeout(() => controller.abort(new DOMException('The nameservice timed out', 'TimeoutError')), SERVER_TIMEOUT_MS)

    if (signal?.aborted) forwardAbort()
    else signal?.addEventListener('abort', forwardAbort, { once: true })

    try {
        return await request(controller.signal)
    }
    finally {
        clearTimeout(timer)
        signal?.removeEventListener('abort', forwardAbort)
    }
}

/**
 * @title useNameserviceClient
 */
export const useNameserviceClient = (): UseNameserviceClientReturn => {
    const { site } = useCoreConfig()
    const client = useOpenapi<NameserviceClient.paths>({
        baseUrl: '/gateway/nameservice/api',
        service: 'nameservice',
        // A 401 skips the redirect and the error report: user/info's own 401 already handles the signed-out player.
        ignoreUnauthorized: true,
        // A 404 is a player the nameservice has no username for yet, not a fault to report.
        ignoreNotFound: true,
    })

    const sessionExpiry = useSessionExpiry()

    /** The player's username; `null` on a 401 or 404. */
    const useGetUsername: UseNameserviceClientReturn['useGetUsername'] = (options = {}) => {
        // Above the fold in the header, so by default it renders with the page rather than swapping in.
        const { enabled = () => !!sessionExpiry.value, lazy = false, server = true } = options

        const getUsername = (signal?: AbortSignal) => client.GET('/public/v1/{jurisdiction}/username', {
            signal,
            params: { path: { jurisdiction: site.jurisdiction.toLowerCase() as NameserviceClient.Jurisdiction } },
        })

        return useQuery(async ({ signal }) => {
            try {
                // The query blocks SSR, so a stalled nameservice must not hang the render.
                const { data } = await (import.meta.server ? withServerTimeout(signal, getUsername) : getUsername(signal))

                return data ?? null
            }
            catch (error) {
                if (OpenapiError.isError(error) && (error.status === 401 || error.status === 404)) return null

                throw error
            }
        }, {
            key: USERNAME_KEY,
            server,
            lazy,
            enabled,
        })
    }

    return { useGetUsername }
}
